import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Col, Collapse, Drawer, Input, Modal, Progress, Row, Select, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { useAuth } from '../../components/common/security/AuthContext';
import { fetchSqlData, updateDataBatch } from '../../services/api';
import { dccList, dccMkdir, dccUploadTar } from '../../services/dccApi';
import { buildTarBlob } from '../../utils/tar';
import type { DccTranslationLogRow, DccTranslationPackage, DccTranslationPackageItem } from '../../types';
import Button from '../../components/common/Button';

const { Text } = Typography;
const { Panel } = Collapse;

const normalizeStr = (v: any) => String(v ?? '').trim();
const basename = (p: any): string => {
  const s = normalizeStr(p).replace(/\\/g, '/');
  if (!s) return '';
  const parts = s.split('/').filter(Boolean);
  return parts[parts.length - 1] || s;
};

const buildUploadRowsFromReturnContents = (
  basePath: string,
  contents: Array<{ key: string; name: string; kind: 'folder' | 'file' }>,
): UploadRow[] => {
  const p = normalizeStr(basePath);
  return (contents || [])
    .filter((c) => c?.kind === 'file')
    .map((c, i) => {
      const nm = normalizeStr(c?.name);
      const stored = [p, nm].filter(Boolean).join('/');
      return {
        key: `${stored}::${i}`,
        file_name: basename(nm || stored),
        stored_path: stored,
      };
    });
};

const unwrapRows = <T,>(resp: any): T[] => {
  if (Array.isArray(resp)) return resp as T[];
  if (Array.isArray(resp?.data)) return resp.data as T[];
  if (Array.isArray(resp?.data?.data)) return resp.data.data as T[];
  return [];
};

type DragDataTransferItem = DataTransferItem & {
  webkitGetAsEntry?: () => any;
};

const attachRelativePath = (file: File, relativePath: string): File => {
  Object.defineProperty(file, 'webkitRelativePath', {
    configurable: true,
    value: relativePath,
  });
  return file;
};

const readDroppedFile = (entry: any): Promise<File> => {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
};

const readDroppedEntries = (reader: any): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const allEntries: any[] = [];
    const readBatch = () => {
      reader.readEntries(
        (entries: any[]) => {
          if (!entries.length) {
            resolve(allEntries);
            return;
          }
          allEntries.push(...entries);
          readBatch();
        },
        (error: unknown) => reject(error)
      );
    };
    readBatch();
  });
};

const flattenDroppedEntry = async (entry: any, parentPath = ''): Promise<File[]> => {
  const currentPath = [parentPath, entry?.name].filter(Boolean).join('/');
  if (entry?.isFile) {
    const file = await readDroppedFile(entry);
    return [attachRelativePath(file, currentPath)];
  }
  if (!entry?.isDirectory) return [];
  const childEntries = await readDroppedEntries(entry.createReader());
  const nestedFiles = await Promise.all(childEntries.map((child) => flattenDroppedEntry(child, currentPath)));
  return nestedFiles.flat();
};

type ExpectedRow = {
  key: string;
  package_item_id: number;
  package_id: string;
  role: string;
  file_path: string;
  file_name: string;
  translation_log_id?: number | null;
};

type UploadRow = {
  key: string;
  file_name: string;
  stored_path: string;
};

type MatchType = 'exact' | 'normalized' | 'manual' | 'unmatched' | 'missing';

type MatchRow = {
  key: string;
  package_item_id?: number;
  translation_log_id?: number | null;
  expected_file_name?: string;
  expected_file_path?: string;
  role?: string;
  uploaded_file_name?: string;
  uploaded_stored_path?: string;
  match_type: MatchType;
};

const buildAlnum = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

const stripSuffixTokens = (nameNoExt: string, tokens: string[]) => {
  let s = nameNoExt;
  const rxParts = tokens
    .map((t) => String(t || '').trim().toLowerCase())
    .filter(Boolean)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!rxParts.length) return s;

  const rx = new RegExp(`(?:[\\s._\u002d\u2013\u2014()]*)(?:${rxParts.join('|')})$`, 'i');
  while (rx.test(s)) {
    s = s.replace(rx, '');
  }
  return s;
};

const buildFileKey = (fileName: string, opts: { suffixTokens: string[] }) => {
  const raw = normalizeStr(fileName);
  const base = basename(raw);
  const dot = base.lastIndexOf('.');
  const nameNoExt = dot >= 0 ? base.slice(0, dot) : base;
  const ext = dot >= 0 ? base.slice(dot + 1) : '';

  const stripped = stripSuffixTokens(nameNoExt, opts.suffixTokens);
  const nameKey = buildAlnum(stripped);
  const extKey = buildAlnum(ext);

  return `${nameKey}.${extKey}`;
};

const sanitizePathSegment = (s: string) => {
  const raw = normalizeStr(s);
  if (!raw) return '';
  return raw
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
};

const buildAutoSubfolderName = (pkg: DccTranslationPackage | undefined) => {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const pid = sanitizePathSegment((pkg as any)?.package_id);
  const parts = ['RET', pid, `${yy}${mm}${dd}`].filter(Boolean);
  return parts.join('-');
};

const DccTranslationReturns: React.FC = () => {
  const { token } = useAuth();

  const [packages, setPackages] = useState<DccTranslationPackage[]>([]);
  const [items, setItems] = useState<DccTranslationPackageItem[]>([]);
  const [translationLogs, setTranslationLogs] = useState<DccTranslationLogRow[]>([]);

  const [selectedPackageId, setSelectedPackageId] = useState<string>('');

  const selectedPackage = useMemo(
    () => packages.find((p: any) => normalizeStr((p as any)?.package_id) === normalizeStr(selectedPackageId)),
    [packages, selectedPackageId]
  );

  const translationLogStatusById = useMemo(() => {
    const m = new Map<number, string>();
    (translationLogs || []).forEach((r: any) => {
      const id = Number(r?.id);
      if (!Number.isFinite(id)) return;
      m.set(id, normalizeStr(r?.status));
    });
    return m;
  }, [translationLogs]);

  const expectedItemsAll = useMemo((): ExpectedRow[] => {
    const pid = normalizeStr(selectedPackageId);
    if (!pid) return [];
    return (items || [])
      .filter((it: any) => normalizeStr(it?.package_id) === pid)
      .map((it: any) => {
        const fp = normalizeStr(it?.file_path);
        return {
          key: String(it?.id ?? `${pid}::${fp}`),
          package_item_id: Number(it?.id),
          package_id: pid,
          role: normalizeStr(it?.role),
          file_path: fp,
          file_name: basename(fp),
          translation_log_id: it?.translation_log_id ?? null,
        };
      });
  }, [items, selectedPackageId]);

  const expectedPrimaryItems = useMemo(() => {
    return (expectedItemsAll || [])
      .filter((it) => String(it?.role || '').toLowerCase() === 'primary')
      .filter((it: any) => {
        const tlid = it?.translation_log_id;
        if (tlid == null) return false;
        const status = String(translationLogStatusById.get(Number(tlid)) || '').toLowerCase();
        return status === 'sent';
      });
  }, [expectedItemsAll, translationLogStatusById]);

  const expectedSupportingItems = useMemo(() => {
    return (expectedItemsAll || []).filter((it) => String(it?.role || '').toLowerCase() === 'supporting');
  }, [expectedItemsAll]);

  const expectedItemsForMatching = useMemo((): ExpectedRow[] => {
    return [...(expectedPrimaryItems || []), ...(expectedSupportingItems || [])];
  }, [expectedPrimaryItems, expectedSupportingItems]);

  const [dccCurrentPath, setDccCurrentPath] = useState('');
  const [dccFolders, setDccFolders] = useState<Array<{ key: string; name: string }>>([]);
  const [dccLoading, setDccLoading] = useState(false);
  const [dccDrawerOpen, setDccDrawerOpen] = useState(false);
  const [dccFolderSearch, setDccFolderSearch] = useState('');

  const [baseTargetPath, setBaseTargetPath] = useState('');
  const [returnFolderPath, setReturnFolderPath] = useState('');
  const [creatingReturnFolder, setCreatingReturnFolder] = useState(false);
  const [returnFolderName, setReturnFolderName] = useState('');

  const [dccReturnContents, setDccReturnContents] = useState<Array<{ key: string; name: string; kind: 'folder' | 'file' }>>([]);
  const [dccReturnContentsLoading, setDccReturnContentsLoading] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadRow[]>([]);
  const [zipProgress, setZipProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [suffixTokensRaw, setSuffixTokensRaw] = useState<string>(() => {
    try {
      return localStorage.getItem('dcc_returns_suffix_tokens') || '';
    } catch {
      return '';
    }
  });

  const [returnsSearch, setReturnsSearch] = useState('');

  const suffixTokens = useMemo(() => {
    const raw = normalizeStr(suffixTokensRaw);
    if (!raw) return [];
    return raw
      .split(/[,;\s]+/)
      .map((t) => String(t || '').trim())
      .filter(Boolean);
  }, [suffixTokensRaw]);

  const returnsSearchTerms = useMemo(() => {
    const q = normalizeStr(returnsSearch).toLowerCase();
    return q ? q.split(/\s+/).filter(Boolean) : [];
  }, [returnsSearch]);

  const matchesReturnsSearch = (hay: any): boolean => {
    if (!returnsSearchTerms.length) return true;
    const s = normalizeStr(hay).toLowerCase();
    return returnsSearchTerms.every((t) => s.includes(t));
  };

  const loadDbOnce = async () => {
    try {
      const [pkgData, itemData] = await Promise.all([
        fetchSqlData('dcc_translation_packages'),
        fetchSqlData('dcc_translation_package_items'),
      ]);

      setPackages(unwrapRows<DccTranslationPackage>(pkgData));
      setItems(unwrapRows<DccTranslationPackageItem>(itemData));
      const logData = await fetchSqlData('dcc_translation_log');
      setTranslationLogs(unwrapRows<DccTranslationLogRow>(logData));
    } catch (e: any) {
      setPackages([]);
      setItems([]);
      setTranslationLogs([]);
      message.error(e?.response?.data?.error || e?.message || 'Failed to load packages');
    }
  };

  const loadDccFolders = async (path = '') => {
    if (!token) return;
    setDccLoading(true);
    try {
      const data = await dccList(path, token);
      const mapped = (data || [])
        .filter((item: string) => {
          const raw = item.replace(/\/$/, '');
          return item.endsWith('/') || !/\.[^./\\]+$/.test(raw);
        })
        .map((item: string) => ({ key: item, name: item.replace(/\/$/, '') }));
      setDccFolders(mapped);
      setDccCurrentPath(path);
    } catch (e: any) {
      setDccFolders([]);
      message.error(e?.message || 'Failed to list DCC folders');
    } finally {
      setDccLoading(false);
    }
  };

  useEffect(() => {
    loadDbOnce();
  }, []);

  useEffect(() => {
    if (!token) return;
    loadDccFolders('');
  }, [token]);

  useEffect(() => {
    setReturnFolderPath('');
    setUploadedFiles([]);
  }, [selectedPackageId]);

  useEffect(() => {
    setReturnFolderName(buildAutoSubfolderName(selectedPackage));
  }, [selectedPackageId, selectedPackage]);

  const loadReturnFolderContents = useCallback(
    async (path: string) => {
      const p = normalizeStr(path);
      if (!p || !token) {
        setDccReturnContents([]);
        setUploadedFiles([]);
        return;
      }
      setDccReturnContentsLoading(true);
      try {
        const data = await dccList(p, token);
        const mapped: Array<{ key: string; name: string; kind: 'folder' | 'file' }> = (data || []).map((item: string) => {
          const isFolder = item.endsWith('/');
          const name = item.replace(/\/$/, '');
          return { key: item, name, kind: isFolder ? 'folder' : 'file' };
        });
        setDccReturnContents(mapped);
        setUploadedFiles(buildUploadRowsFromReturnContents(p, mapped));
      } catch (e: any) {
        setDccReturnContents([]);
        setUploadedFiles([]);
        message.error(e?.message || 'Failed to list return folder');
      } finally {
        setDccReturnContentsLoading(false);
      }
    },
    [token]
  );

  const packageOptions = useMemo(() => {
    const allowed = new Set(['sent', 'partial_returned']);
    return (packages || [])
      .filter((p: any) => allowed.has(String(p?.status || '').toLowerCase()))
      .map((p: any) => normalizeStr(p?.package_id))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((pid) => ({ value: pid, label: pid }));
  }, [packages]);

  const filteredDccFolders = useMemo(() => {
    const q = normalizeStr(dccFolderSearch).toLowerCase();
    if (!q) return dccFolders;
    return (dccFolders || []).filter((f) => String(f?.name || '').toLowerCase().includes(q));
  }, [dccFolderSearch, dccFolders]);

  const handleEnterFolder = (name: string) => {
    const nextPath = dccCurrentPath ? `${dccCurrentPath}/${name}` : name;
    loadDccFolders(nextPath);
  };

  const handleGoUp = () => {
    if (!dccCurrentPath) return;
    const parts = dccCurrentPath.split('/');
    parts.pop();
    loadDccFolders(parts.join('/'));
  };

  const openDccDrawer = async () => {
    if (!token) {
      message.warning('Login required');
      return;
    }
    setDccDrawerOpen(true);
    setDccFolderSearch('');
    await loadDccFolders(normalizeStr(baseTargetPath));
  };

  const applyDrawerPathAsBase = () => {
    setBaseTargetPath(dccCurrentPath);
    setDccDrawerOpen(false);
  };

  const createReturnFolder = async () => {
    const pid = normalizeStr(selectedPackageId);
    if (!pid) {
      message.info('Select a package');
      return;
    }
    if (!token) {
      message.warning('Login required');
      return;
    }

    const base = normalizeStr(baseTargetPath);
    if (!base) {
      message.warning('Select a base DCC folder first');
      return;
    }
    const folderName = sanitizePathSegment(returnFolderName);
    if (!folderName) {
      message.error('Failed to build return folder name');
      return;
    }

    const path = [base, folderName].filter(Boolean).join('/');

    try {
      setCreatingReturnFolder(true);
      await dccMkdir(path, token);
      setReturnFolderPath(path);
      message.success(`Return folder created: ${path}`);
      await loadDccFolders(base);
      await loadReturnFolderContents(path);
    } catch (e: any) {
      message.error(e?.message || 'Failed to create return folder');
    } finally {
      setCreatingReturnFolder(false);
    }
  };

  const zipAndUpload = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      if (!token) {
        message.warning('Login required');
        return;
      }
      const target = normalizeStr(returnFolderPath);
      if (!target) {
        message.info('Create/select a return folder first');
        return;
      }

      setUploading(true);
      setZipProgress(0);
      const hide = message.loading('Uploading...', 0);

      try {
        const tarBlob = await buildTarBlob(files, { onProgress: setZipProgress });
        setZipProgress(95);
        await dccUploadTar(target, tarBlob, token);
        setZipProgress(100);
        hide();
        message.success(`Uploaded ${files.length} file(s)`);
        await loadReturnFolderContents(target);
      } catch (e: any) {
        hide();
        message.error(e?.message || 'Upload failed');
      } finally {
        setUploading(false);
        window.setTimeout(() => setZipProgress(0), 1500);
      }
    },
    [loadReturnFolderContents, returnFolderPath, token]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      if (uploading) return;

      const items = Array.from(e.dataTransfer?.items || []) as DragDataTransferItem[];
      const entries = items.map((item) => item.webkitGetAsEntry?.()).filter(Boolean);
      const hasDirectory = (entries as any[]).some((entry) => entry?.isDirectory);

      if (entries.length && hasDirectory) {
        const nested = await Promise.all(entries.map((entry: any) => flattenDroppedEntry(entry)));
        await zipAndUpload(nested.flat());
        return;
      }

      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length) await zipAndUpload(files);
    },
    [uploading, zipAndUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  }, [dragOver]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragOver(false);
  }, []);

  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      if (files.length) await zipAndUpload(files);
    },
    [zipAndUpload]
  );

  const handleDropZoneClick = useCallback(() => {
    if (uploading) return;
    if (!token) return;
    if (!normalizeStr(returnFolderPath)) return;
    fileInputRef.current?.click();
  }, [returnFolderPath, token, uploading]);

  const { matchRows, unmatchedUploads, missingExpected } = useMemo(() => {
    const expected = expectedItemsForMatching || [];
    const uploads = uploadedFiles || [];

    const expectedByExact = new Map<string, ExpectedRow>(); // full filename lowercased
    const expectedByKey = new Map<string, ExpectedRow[]>(); // normalized key -> expected list
    const expectedFlat: Array<{ key: string; ex: ExpectedRow }> = [];

    expected.forEach((ex) => {
      expectedByExact.set(ex.file_name.toLowerCase(), ex);
      const key = buildFileKey(ex.file_name, { suffixTokens });
      const arr = expectedByKey.get(key) || [];
      arr.push(ex);
      expectedByKey.set(key, arr);
      expectedFlat.push({ key, ex });
    });

    const usedExpected = new Set<string>();
    const usedUploads = new Set<string>();
    const rows: MatchRow[] = [];

    // 1) Exact filename matches first (spare them)
    uploads.forEach((up) => {
      const exact = expectedByExact.get(up.file_name.toLowerCase());
      if (exact && !usedExpected.has(exact.key)) {
        usedExpected.add(exact.key);
        usedUploads.add(up.key);
        rows.push({
          key: `m::exact::${exact.key}::${up.key}`,
          package_item_id: exact.package_item_id,
          translation_log_id: exact.translation_log_id,
          expected_file_name: exact.file_name,
          expected_file_path: exact.file_path,
          role: exact.role,
          uploaded_file_name: up.file_name,
          uploaded_stored_path: up.stored_path,
          match_type: 'exact',
        });
      }
    });

    // 2) Normalized exact key matches with a unique candidate
    uploads.forEach((up) => {
      if (usedUploads.has(up.key)) return;
      const upKey = buildFileKey(up.file_name, { suffixTokens });
      const candidates = (expectedByKey.get(upKey) || []).filter((c) => !usedExpected.has(c.key));
      if (candidates.length === 1) {
        const candidate = candidates[0];
        usedExpected.add(candidate.key);
        usedUploads.add(up.key);
        rows.push({
          key: `m::norm::${candidate.key}::${up.key}`,
          package_item_id: candidate.package_item_id,
          translation_log_id: candidate.translation_log_id,
          expected_file_name: candidate.file_name,
          expected_file_path: candidate.file_path,
          role: candidate.role,
          uploaded_file_name: up.file_name,
          uploaded_stored_path: up.stored_path,
          match_type: 'normalized',
        });
      }
    });

    const missing: MatchRow[] = expected
      .filter((ex) => !usedExpected.has(ex.key))
      .map((ex) => ({
        key: `missing::${ex.key}`,
        package_item_id: ex.package_item_id,
        translation_log_id: ex.translation_log_id,
        expected_file_name: ex.file_name,
        expected_file_path: ex.file_path,
        role: ex.role,
        match_type: 'missing',
      }));

    const unmatched: MatchRow[] = uploads
      .filter((u) => !usedUploads.has(u.key))
      .map((u) => ({
        key: `unmatched::${u.key}`,
        uploaded_file_name: u.file_name,
        uploaded_stored_path: u.stored_path,
        match_type: 'unmatched',
      }));

    return { matchRows: rows, missingExpected: missing, unmatchedUploads: unmatched };
  }, [expectedItemsForMatching, suffixTokens, uploadedFiles]);

  const [manualMatches, setManualMatches] = useState<MatchRow[]>([]);

  const usedUploadKeys = useMemo(() => {
    const s = new Set<string>();
    (matchRows || []).forEach((m) => {
      if (m.uploaded_stored_path) s.add(String(m.uploaded_stored_path));
    });
    (manualMatches || []).forEach((m) => {
      if (m.uploaded_stored_path) s.add(String(m.uploaded_stored_path));
    });
    return s;
  }, [matchRows, manualMatches]);

  const availableUnmatchedUploads = useMemo(() => {
    return (unmatchedUploads || []).filter((u) => !usedUploadKeys.has(String(u.uploaded_stored_path || '')));
  }, [unmatchedUploads, usedUploadKeys]);

  const missingPrimary = useMemo(() => {
    return (missingExpected || []).filter((m) => String(m.role || '').toLowerCase() === 'primary');
  }, [missingExpected]);

  const missingSupporting = useMemo(() => {
    return (missingExpected || []).filter((m) => String(m.role || '').toLowerCase() === 'supporting');
  }, [missingExpected]);

  const matchedPairs = useMemo(() => {
    const all = [...(matchRows || []), ...(manualMatches || [])];
    return all
      .filter((m) => m.match_type === 'exact' || m.match_type === 'normalized' || m.match_type === 'manual')
      .map((m) => ({
        key: m.key,
        match_type: m.match_type,
        role: m.role,
        expected_file_name: m.expected_file_name,
        uploaded_file_name: m.uploaded_file_name,
        uploaded_stored_path: m.uploaded_stored_path,
      }));
  }, [manualMatches, matchRows]);

  const filteredReturnContents = useMemo(() => {
    if (!returnsSearchTerms.length) return dccReturnContents;
    return (dccReturnContents || []).filter((r) => {
      const hay = [r?.name].map((v) => normalizeStr(v)).join(' ');
      return matchesReturnsSearch(hay);
    });
  }, [dccReturnContents, returnsSearchTerms]);

  const filteredMatchedPairs = useMemo(() => {
    if (!returnsSearchTerms.length) return matchedPairs;
    return (matchedPairs || []).filter((r: any) => {
      const hay = [r?.expected_file_name, r?.uploaded_file_name, r?.uploaded_stored_path].map((v) => normalizeStr(v)).join(' ');
      return matchesReturnsSearch(hay);
    });
  }, [matchedPairs, returnsSearchTerms]);

  const filteredMissingPrimaryForDisplay = useMemo(() => {
    const base = (missingPrimary || []).filter((m) => !manualMatches.some((mm) => mm.package_item_id === m.package_item_id));
    if (!returnsSearchTerms.length) return base;
    return base.filter((r: any) => {
      const hay = [r?.expected_file_name, r?.expected_file_path].map((v) => normalizeStr(v)).join(' ');
      return matchesReturnsSearch(hay);
    });
  }, [manualMatches, missingPrimary, returnsSearchTerms]);

  const filteredMissingSupportingForDisplay = useMemo(() => {
    const base = (missingSupporting || []).filter((m) => !manualMatches.some((mm) => mm.package_item_id === m.package_item_id));
    if (!returnsSearchTerms.length) return base;
    return base.filter((r: any) => {
      const hay = [r?.expected_file_name, r?.expected_file_path].map((v) => normalizeStr(v)).join(' ');
      return matchesReturnsSearch(hay);
    });
  }, [manualMatches, missingSupporting, returnsSearchTerms]);

  const filteredAvailableUnmatchedUploads = useMemo(() => {
    if (!returnsSearchTerms.length) return availableUnmatchedUploads;
    return (availableUnmatchedUploads || []).filter((r: any) => {
      const hay = [r?.uploaded_file_name, r?.uploaded_stored_path].map((v) => normalizeStr(v)).join(' ');
      return matchesReturnsSearch(hay);
    });
  }, [availableUnmatchedUploads, returnsSearchTerms]);

  const filteredManualMatches = useMemo(() => {
    if (!returnsSearchTerms.length) return manualMatches;
    return (manualMatches || []).filter((r: any) => {
      const hay = [r?.expected_file_name, r?.expected_file_path, r?.uploaded_file_name, r?.uploaded_stored_path]
        .map((v) => normalizeStr(v))
        .join(' ');
      return matchesReturnsSearch(hay);
    });
  }, [manualMatches, returnsSearchTerms]);

  const [selectedMissingPrimaryKey, setSelectedMissingPrimaryKey] = useState<string>('');
  const [selectedMissingSupportingKey, setSelectedMissingSupportingKey] = useState<string>('');
  const [selectedUnmatchedUploadKey, setSelectedUnmatchedUploadKey] = useState<string>('');

  useEffect(() => {
    setManualMatches([]);
    setSelectedMissingPrimaryKey('');
    setSelectedMissingSupportingKey('');
    setSelectedUnmatchedUploadKey('');
  }, [selectedPackageId, uploadedFiles.length]);

  const addManualMatch = (expectedKey: string) => {
    const ex = (missingExpected || []).find((m) => m.key === expectedKey);
    const up = (availableUnmatchedUploads || []).find((u) => u.key === selectedUnmatchedUploadKey);
    if (!ex || !up) {
      message.info('Select an expected item and an unmatched upload');
      return;
    }
    setManualMatches((prev) => {
      const already = prev.find((p) => p.key === `manual::${ex.key}::${up.key}`);
      if (already) return prev;
      return [
        ...prev,
        {
          key: `manual::${ex.key}::${up.key}`,
          package_item_id: ex.package_item_id,
          translation_log_id: ex.translation_log_id,
          expected_file_name: ex.expected_file_name,
          expected_file_path: ex.expected_file_path,
          role: ex.role,
          uploaded_file_name: up.uploaded_file_name,
          uploaded_stored_path: up.uploaded_stored_path,
          match_type: 'manual',
        },
      ];
    });
    setSelectedUnmatchedUploadKey('');
    setSelectedMissingPrimaryKey('');
    setSelectedMissingSupportingKey('');
  };

  const removeManualMatch = (key: string) => {
    setManualMatches((prev) => (prev || []).filter((m) => m.key !== key));
  };

  const primaryMatchedCount = useMemo(() => {
    const matchedPrimaryExpectedKeys = new Set<string>();
    [...(matchRows || []), ...(manualMatches || [])]
      .filter((m) => String(m.role || '').toLowerCase() === 'primary')
      .forEach((m) => {
        if (m.package_item_id != null) matchedPrimaryExpectedKeys.add(String(m.package_item_id));
      });
    return matchedPrimaryExpectedKeys.size;
  }, [matchRows, manualMatches]);

  const primaryMissingCount = useMemo(() => {
    const manualPrimaryIds = new Set<string>();
    (manualMatches || [])
      .filter((m) => String(m.role || '').toLowerCase() === 'primary')
      .forEach((m) => {
        if (m.package_item_id != null) manualPrimaryIds.add(String(m.package_item_id));
      });
    const rawMissing = missingPrimary || [];
    const stillMissing = rawMissing.filter((m) => !manualPrimaryIds.has(String(m.package_item_id)));
    return stillMissing.length;
  }, [manualMatches, missingPrimary]);

  const confirmAndUpdateLog = async () => {
    const pid = normalizeStr(selectedPackageId);
    if (!pid) {
      message.info('Select a package');
      return;
    }

    const allMatches = [...matchRows, ...manualMatches];
    const vendorForUpdate = normalizeStr((selectedPackage as any)?.vendor);
    const primaryMatches = allMatches
      .filter((m) => m.translation_log_id != null && m.uploaded_stored_path)
      .map((m) => ({
        id: m.translation_log_id as any,
        data: {
          status: 'completed',
          translated_file_path: m.uploaded_stored_path,
          completed_at: new Date().toISOString(),
          ...(vendorForUpdate ? { vendor: vendorForUpdate } : {}),
        },
      }));

    if (!primaryMatches.length) {
      message.warning('No matched primary items with translation_log_id to update');
      return;
    }

    try {
      const updates = primaryMatches.map((u) => ({ id: u.id, data: u.data }));
      await updateDataBatch('dcc_translation_log', { id_field: 'id', updates });

      if (primaryMissingCount === 0) {
        await updateDataBatch('dcc_translation_packages', {
          id_field: 'package_id',
          ids: [pid],
          data: { status: 'returned' },
        });
      }
      message.success(`Updated ${updates.length} translation log row(s) as completed`);
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Failed to update translation log');
    }
  };

  const submitPartialReturn = async () => {
    const pid = normalizeStr(selectedPackageId);
    if (!pid) {
      message.info('Select a package');
      return;
    }

    Modal.confirm({
      title: 'Submit partial return',
      content: `This will mark matched primary items as completed and set the package status to partial_returned. Continue?`,
      okText: 'Submit partial',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        const allMatches = [...matchRows, ...manualMatches];
        const vendorForUpdate = normalizeStr((selectedPackage as any)?.vendor);
        const primaryMatches = allMatches
          .filter((m) => String(m.role || '').toLowerCase() === 'primary')
          .filter((m) => m.translation_log_id != null && m.uploaded_stored_path)
          .map((m) => ({
            id: m.translation_log_id as any,
            data: {
              status: 'completed',
              translated_file_path: m.uploaded_stored_path,
              completed_at: new Date().toISOString(),
              ...(vendorForUpdate ? { vendor: vendorForUpdate } : {}),
            },
          }));

        if (!primaryMatches.length) {
          message.warning('No matched primary items with translation_log_id to update');
          return;
        }

        try {
          const updates = primaryMatches.map((u) => ({ id: u.id, data: u.data }));
          await updateDataBatch('dcc_translation_log', { id_field: 'id', updates });

          await updateDataBatch('dcc_translation_packages', {
            id_field: 'package_id',
            ids: [pid],
            data: { status: 'partial_returned' },
          });

          message.success(`Submitted partial return (${updates.length} completed)`);
        } catch (e: any) {
          message.error(e?.response?.data?.error || e?.message || 'Failed to submit partial return');
        }
      },
    });
  };

  return (
    <Card style={{ width: '100%', maxWidth: '100%' }}>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Row gutter={[8, 8]} align="middle" wrap>
          <Col xs={24} md={4}>
            <Select
              showSearch
              allowClear
              placeholder="Package"
              value={selectedPackageId || undefined}
              onChange={(v) => setSelectedPackageId(normalizeStr(v))}
              options={packageOptions}
              style={{ width: '100%' }}
              filterOption={(input, opt) => String(opt?.value || '').toLowerCase().includes(String(input || '').toLowerCase())}
            />
          </Col>
          <Col xs={24} md={4}>
            <Input
              value={baseTargetPath}
              readOnly
              placeholder="Base folder (browse)"
              onClick={openDccDrawer}
            />
          </Col>
          <Col xs={24} md={6}>
            <Input value={returnFolderName} onChange={(e) => setReturnFolderName(e.target.value)} placeholder="Return subfolder" allowClear />
          </Col>
          <Col xs={24} md={2}>
            <Button
              action="add"
              type="primary"
              onClick={createReturnFolder}
              loading={creatingReturnFolder}
              disabled={!selectedPackageId || !token || !normalizeStr(baseTargetPath)}
              style={{ width: '100%' }}
            >
              Create
            </Button>
          </Col>
          <Col xs={24} md={2}>
            <Input value={suffixTokensRaw} onChange={(e) => setSuffixTokensRaw(e.target.value)} placeholder="Suffix" allowClear />
          </Col>
          <Col xs={24} md={6}>
            <Input
              allowClear
              placeholder="Search (return contents/matching)"
              value={returnsSearch}
              onChange={(e) => setReturnsSearch(e.target.value)}
            />
          </Col>
        </Row>

        <Space wrap align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text type="secondary">
            Vendor: <Text strong>{normalizeStr((selectedPackage as any)?.vendor) || '-'}</Text>
            {'  '}Pair: <Text strong>{normalizeStr((selectedPackage as any)?.translationpair) || '-'}</Text>
            {'  '}Sent: <Text strong>{normalizeStr((selectedPackage as any)?.sent_date) || '-'}</Text>
          </Text>

          <Space wrap align="center">
            <Button
              size="small"
              onClick={() => loadReturnFolderContents(returnFolderPath)}
              disabled={!returnFolderPath || dccReturnContentsLoading}
            >
              Refresh return folder
            </Button>
            <Text type="secondary">
              Return folder: <Text code>{returnFolderPath || '-'}</Text>
            </Text>
            <Text type="secondary">
              Items: <Text code>{dccReturnContents.length}</Text>
            </Text>
          </Space>
        </Space>

        {!token && <Alert type="warning" message="Login required for DCC folder browsing/upload" showIcon style={{ marginBottom: 0 }} />}

        <Row gutter={[8, 8]}>
          <Col xs={24} md={24}>
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
                disabled={!returnFolderPath || uploading}
              />

              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleDropZoneClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleDropZoneClick();
                }}
                style={{
                  border: `2px dashed ${dragOver ? '#1677ff' : '#d9d9d9'}`,
                  borderRadius: 8,
                  padding: '18px 16px',
                  textAlign: 'center',
                  cursor: !returnFolderPath || uploading || !token ? 'not-allowed' : 'pointer',
                  background: dragOver ? 'rgba(22, 119, 255, 0.06)' : 'rgba(0,0,0,0.01)',
                  opacity: !returnFolderPath || uploading || !token ? 0.5 : 1,
                }}
              >
                <div style={{ fontWeight: 500 }}>
                  Click or drop files here
                </div>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                  {returnFolderPath ? `Target: ${returnFolderPath}` : 'Create/select a return folder first'}
                </div>
              </div>

              {zipProgress > 0 && (
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  <Text style={{ fontSize: 12, color: '#8c8c8c' }}>
                    {zipProgress < 90 ? 'Reading files…' : zipProgress < 95 ? 'Building TAR…' : 'Uploading…'}
                  </Text>
                  <Progress percent={zipProgress} status={zipProgress < 100 ? 'active' : 'success'} size="small" />
                </Space>
              )}

              <Collapse defaultActiveKey={['return_contents']}>
                <Panel
                  header={
                    <div
                      style={{
                        padding: '6px 10px',
                        borderRadius: 6,
                        fontWeight: 700,
                        color: '#0958d9',
                        background: 'rgba(22, 119, 255, 0.10)',
                        border: '1px solid rgba(22, 119, 255, 0.18)',
                      }}
                    >
                      Return folder contents
                    </div>
                  }
                  key="return_contents"
                >
                  <Spin spinning={dccReturnContentsLoading}>
                    <Table
                      size="small"
                      pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                      rowKey="key"
                      columns={[
                        { title: 'Type', dataIndex: 'kind', key: 'kind', width: 90 },
                        { title: 'Name', dataIndex: 'name', key: 'name' },
                      ]}
                      dataSource={filteredReturnContents}
                    />
                  </Spin>
                </Panel>
              </Collapse>

              <div>
                <Text type="secondary">
                  Uploaded: <Text code>{uploadedFiles.length}</Text>
                  {'  '}Expected primary: <Text code>{expectedPrimaryItems.length}</Text>
                  {'  '}Matched primary: <Text code>{primaryMatchedCount}</Text>
                  {'  '}Missing primary: <Text strong>{primaryMissingCount}</Text>
                  {'  '}Expected supporting: <Text code>{expectedSupportingItems.length}</Text>
                </Text>
              </div>

              <Collapse defaultActiveKey={['needs_attention']} style={{ marginTop: 8 }}>
                <Panel
                  header={
                    <div
                      style={{
                        padding: '6px 10px',
                        borderRadius: 6,
                        fontWeight: 700,
                        color: '#237804',
                        background: 'rgba(82, 196, 26, 0.14)',
                        border: '1px solid rgba(82, 196, 26, 0.22)',
                      }}
                    >
                      Matched pairs ({matchedPairs.length})
                    </div>
                  }
                  key="matched_pairs"
                >
                  <Table
                    size="small"
                    pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                    rowKey="key"
                    columns={[
                      {
                        title: 'Type',
                        dataIndex: 'match_type',
                        key: 'match_type',
                        width: 140,
                        render: (_: any, r: any) => {
                          const t = String(r?.match_type || '').toLowerCase();
                          const color = t === 'exact' ? 'green' : t === 'normalized' ? 'blue' : 'gold';
                          return <Tag color={color}>{t === 'normalized' ? 'normalized' : String(r?.match_type || '')}</Tag>;
                        },
                      },
                      { title: 'Role', dataIndex: 'role', key: 'role', width: 110 },
                      {
                        title: 'Expected',
                        dataIndex: 'expected_file_name',
                        key: 'expected_file_name',
                        render: (v: any) => (
                          <div style={{ fontSize: 12, lineHeight: '16px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {normalizeStr(v)}
                          </div>
                        ),
                      },
                      {
                        title: 'Uploaded',
                        dataIndex: 'uploaded_file_name',
                        key: 'uploaded_file_name',
                        render: (v: any) => (
                          <div style={{ fontSize: 12, lineHeight: '16px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {normalizeStr(v)}
                          </div>
                        ),
                      },
                    ]}
                    dataSource={filteredMatchedPairs}
                  />
                </Panel>
                <Panel
                  header={
                    <div
                      style={{
                        padding: '6px 10px',
                        borderRadius: 6,
                        fontWeight: 700,
                        color: '#ad4e00',
                        background: 'rgba(250, 173, 20, 0.16)',
                        border: '1px solid rgba(250, 173, 20, 0.26)',
                      }}
                    >
                      Needs attention (Primary required)
                    </div>
                  }
                  key="needs_attention"
                >
                  <Row gutter={[8, 8]}>
                    <Col xs={24} md={11}>
                      <Table
                        size="small"
                        pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                        rowKey="key"
                        rowSelection={{
                          type: 'radio',
                          selectedRowKeys: selectedMissingPrimaryKey ? [selectedMissingPrimaryKey] : [],
                          onChange: (keys) => setSelectedMissingPrimaryKey(String(keys?.[0] || '')),
                        }}
                        columns={[
                          {
                            title: 'Expected',
                            dataIndex: 'expected_file_name',
                            key: 'expected_file_name',
                            render: (v: any) => (
                              <div style={{ fontSize: 12, lineHeight: '16px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {normalizeStr(v)}
                              </div>
                            ),
                          },
                        ]}
                        dataSource={filteredMissingPrimaryForDisplay}
                      />
                    </Col>
                    <Col xs={24} md={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Button
                        type="primary"
                        disabled={!selectedMissingPrimaryKey || !selectedUnmatchedUploadKey}
                        onClick={() => addManualMatch(selectedMissingPrimaryKey)}
                      >
                        Pair
                      </Button>
                    </Col>
                    <Col xs={24} md={11}>
                      <Table
                        size="small"
                        pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                        rowKey="key"
                        rowSelection={{
                          type: 'radio',
                          selectedRowKeys: selectedUnmatchedUploadKey ? [selectedUnmatchedUploadKey] : [],
                          onChange: (keys) => setSelectedUnmatchedUploadKey(String(keys?.[0] || '')),
                        }}
                        columns={[
                          {
                            title: 'Uploaded',
                            dataIndex: 'uploaded_file_name',
                            key: 'uploaded_file_name',
                            render: (v: any) => (
                              <div style={{ fontSize: 12, lineHeight: '16px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {normalizeStr(v)}
                              </div>
                            ),
                          },
                        ]}
                        dataSource={filteredAvailableUnmatchedUploads}
                      />
                    </Col>
                  </Row>

                  <div style={{ marginTop: 8 }}>
                    <Button danger disabled={!manualMatches.length} onClick={() => setManualMatches([])}>
                      Clear manual pairs
                    </Button>
                  </div>
                </Panel>

                <Panel
                  header={
                    <div
                      style={{
                        padding: '6px 10px',
                        borderRadius: 6,
                        fontWeight: 700,
                        color: '#006d75',
                        background: 'rgba(19, 194, 194, 0.14)',
                        border: '1px solid rgba(19, 194, 194, 0.22)',
                      }}
                    >
                      Supporting (optional)
                    </div>
                  }
                  key="supporting"
                >
                  <Row gutter={[8, 8]}>
                    <Col xs={24} md={11}>
                      <Table
                        size="small"
                        pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                        rowKey="key"
                        rowSelection={{
                          type: 'radio',
                          selectedRowKeys: selectedMissingSupportingKey ? [selectedMissingSupportingKey] : [],
                          onChange: (keys) => setSelectedMissingSupportingKey(String(keys?.[0] || '')),
                        }}
                        columns={[
                          {
                            title: 'Expected',
                            dataIndex: 'expected_file_name',
                            key: 'expected_file_name',
                            render: (v: any) => (
                              <div style={{ fontSize: 12, lineHeight: '16px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {normalizeStr(v)}
                              </div>
                            ),
                          },
                        ]}
                        dataSource={filteredMissingSupportingForDisplay}
                      />
                    </Col>
                    <Col xs={24} md={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Button
                        disabled={!selectedMissingSupportingKey || !selectedUnmatchedUploadKey}
                        onClick={() => addManualMatch(selectedMissingSupportingKey)}
                      >
                        Pair
                      </Button>
                    </Col>
                    <Col xs={24} md={11}>
                      <Table
                        size="small"
                        pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                        rowKey="key"
                        rowSelection={{
                          type: 'radio',
                          selectedRowKeys: selectedUnmatchedUploadKey ? [selectedUnmatchedUploadKey] : [],
                          onChange: (keys) => setSelectedUnmatchedUploadKey(String(keys?.[0] || '')),
                        }}
                        columns={[
                          {
                            title: 'Uploaded',
                            dataIndex: 'uploaded_file_name',
                            key: 'uploaded_file_name',
                            render: (v: any) => (
                              <div style={{ fontSize: 12, lineHeight: '16px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {normalizeStr(v)}
                              </div>
                            ),
                          },
                        ]}
                        dataSource={filteredAvailableUnmatchedUploads}
                      />
                    </Col>
                  </Row>
                </Panel>
              </Collapse>

              <Card size="small" title="Manual pairs" style={{ marginTop: 8 }} bodyStyle={{ padding: 10 }}>
                <Table
                  size="small"
                  pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                  rowKey="key"
                  columns={[
                    { title: 'Role', dataIndex: 'role', key: 'role', width: 110 },
                    {
                      title: 'Expected',
                      dataIndex: 'expected_file_name',
                      key: 'expected_file_name',
                      render: (v: any) => (
                        <div style={{ fontSize: 12, lineHeight: '16px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {normalizeStr(v)}
                        </div>
                      ),
                    },
                    {
                      title: 'Uploaded',
                      dataIndex: 'uploaded_file_name',
                      key: 'uploaded_file_name',
                      render: (v: any) => (
                        <div style={{ fontSize: 12, lineHeight: '16px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {normalizeStr(v)}
                        </div>
                      ),
                    },
                    {
                      title: 'Action',
                      key: 'action',
                      width: 100,
                      render: (_: any, r: any) => (
                        <Button action="edit" size="small" danger onClick={() => removeManualMatch(String(r?.key || ''))}>
                          Unpair
                        </Button>
                      ),
                    },
                  ]}
                  dataSource={filteredManualMatches}
                />
              </Card>

              <Space wrap>
                <Button
                  action="edit"
                  type="primary"
                  onClick={confirmAndUpdateLog}
                  disabled={!selectedPackageId || primaryMissingCount !== 0}
                >
                  Confirm + mark completed
                </Button>
                <Button
                  action="edit"
                  onClick={submitPartialReturn}
                  disabled={!selectedPackageId || primaryMatchedCount === 0}
                >
                  Submit partial
                </Button>
              </Space>
            </Space>
          </Col>
        </Row>

        <Drawer
          title="Select base DCC folder"
          open={dccDrawerOpen}
          onClose={() => setDccDrawerOpen(false)}
          width={560}
          destroyOnClose
        >
          <Spin spinning={dccLoading}>
            <Space direction="vertical" style={{ width: '100%' }} size={6}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }} size={6}>
                <Space size={6} wrap>
                  <Button size="small" onClick={handleGoUp} disabled={!dccCurrentPath}>
                    Up
                  </Button>
                  <Button size="small" onClick={() => loadDccFolders(dccCurrentPath)}>
                    Refresh
                  </Button>
                  <Input
                    size="small"
                    allowClear
                    placeholder="Search folders"
                    value={dccFolderSearch}
                    onChange={(e) => setDccFolderSearch(e.target.value)}
                    style={{ width: 220 }}
                  />
                </Space>
                <Button size="small" type="primary" onClick={applyDrawerPathAsBase}>
                  Use this folder
                </Button>
              </Space>

              <Text type="secondary" style={{ fontSize: 12 }}>
                Current: <Text code>{dccCurrentPath || '/'}</Text>
              </Text>
              <Table
                size="small"
                pagination={false}
                rowKey="key"
                scroll={{ y: 520 }}
                columns={[
                  {
                    title: 'Folder',
                    dataIndex: 'name',
                    key: 'name',
                    render: (v: any, r: any) => (
                      <Button type="link" onClick={() => handleEnterFolder(r.name)}>
                        {String(v || '')}
                      </Button>
                    ),
                  },
                ]}
                dataSource={filteredDccFolders}
              />
            </Space>
          </Spin>
        </Drawer>
      </Space>
    </Card>
  );
};

export default DccTranslationReturns;
